// ** React
import React from 'react';

// ** Custom Components
import Section from '@/components/common/Section';
import { GraphTableSwitch } from '@/components/common/GraphTableSwitch';

// ** Types
import type { StrapiComponentProps } from '@/types/strapi';
import type { GraphTableData, GraphTableSeries, GraphTableSeriesStyle } from '@/components/common/GraphTableSwitch';

type GraphicBlockProps = {
	graphicContent: {
		category: string;
		firstValue: number | null;
		firstValueTitle: string | null;
		secondValue: number | null;
		secondValueTitle: string | null;
		thirdValue: number | null;
		thirdValueTitle: string | null;
		fourthValue: number | null;
		fourthValueTitle: string | null;
		fifthValue: number | null;
		fifthValueTitle: string | null;
		__typename: string;
	}[];
	graphicTitle: string;
};
type ComponentBlocksGraphicBlockProps = StrapiComponentProps<GraphicBlockProps>;

const ComponentBlocksGraphicBlock = ({
	containerSx,
	graphicTitle,
	graphicContent
}: ComponentBlocksGraphicBlockProps) => {
	const formattedData = React.useMemo((): GraphTableData => {
		const seriesTitles = new Set<string>();
		const valueKeys = ['first', 'second', 'third', 'fourth', 'fifth'] as const;

		graphicContent.forEach((item) => {
			valueKeys.forEach((key) => {
				const title = item[`${key}ValueTitle` as keyof typeof item];
				if (typeof title === 'string' && title.trim() !== '') {
					seriesTitles.add(title);
				}
			});
		});

		const firstCategory = graphicContent[0]?.category;
		const isNumeric = !Number.isNaN(Number(firstCategory));
		const xAxisLabel = isNumeric ? 'Jahr' : 'Kategorie';

		const categories = graphicContent.map((item) => item.category);

		const styles: GraphTableSeriesStyle[] = ['solidOrange', 'solidGrey', 'dashedOrange', 'dashedGrey'];

		// 3. Map Series
		const series: GraphTableSeries[] = Array.from(seriesTitles).map((title, index) => ({
			name: title,
			style: styles[index % styles.length],
			data: graphicContent.map((item) => {
				for (const key of valueKeys) {
					if (item[`${key}ValueTitle` as keyof typeof item] === title) {
						const val = item[`${key}Value` as keyof typeof item];
						return val !== null ? Number(val) : 0;
					}
				}
				return 0;
			})
		}));

		return {
			title: graphicTitle,
			xAxisTitle: xAxisLabel,
			yAxisTitle: 'Anzahl',
			categories,
			series
		};
	}, [graphicContent, graphicTitle]);

	return (
		<Section containerSx={containerSx}>
			<GraphTableSwitch data={formattedData} initialView="graph" />
		</Section>
	);
};

export default ComponentBlocksGraphicBlock;
